// 게임 화면. 4단계: 방을 하나 옮길 때마다 여기에 연결해 배포 화면에서 확인한다 (조용한 온천, 라바 라군, 찜질방).
// 아래쪽 버튼은 임시 최소 UI이고, 5단계에서 모바일 하단 바(Actions · Grooming · Talk · Go to)로 새로 만든다.
import * as THREE from 'three';
import { CHAT_LETTERS, CHAT_MAX, MENUS, isAllowedChat, type DishId, type LagoonMaskId } from '@capy/shared';
import { createStage } from './engine/renderer';
import { createLights } from './engine/lights';
import { createFollowCamera } from './engine/camera';
import { attachInput, hitGround, rayFrom } from './engine/input';
import { createTapMarker } from './engine/tapMarker';
import { startEmote } from './capy/pose';
import { checkClimb } from './capy/stackRules';
import { hopDown, shakeOff, startClimb, type LandingFn } from './capy/stacking';
import { setFace, setHand } from './capy/equip';
import { LEGACY } from './engine/lights';
import type { Capy, EmoteKey } from './capy/agent';
import { cleanupEating, dropCarry } from './food/eating';
import type { NavGrid } from './engine/nav';
import { createQuietRoom } from './rooms/quiet';
import { tickSnow } from './rooms/quiet/nature';
import { quietTap, startBath, startExit, randomLand, releaseSlot, releaseWait } from './rooms/quiet/bots';
import { exitRoom, goIn, isDoorTap, orderDish, DOOR } from './rooms/quiet/ryokan';
import { createLagoonRoom, type LagoonNote, type LagoonRoom } from './rooms/lagoon';
import { PHRASES, applyBarItem, goToBar, goToRinse, lagoonTap, type BarJob } from './rooms/lagoon/bots';
import { lagoonRandom } from './rooms/lagoon/layout';
import { createJjimRoom, type JjimRoom } from './rooms/jjim';
import { bathDo, deckTap, goDeck, goRoom, goToShop, jjimTap, placeOrder, playAt, startRollJ, toggleLieJ, type BathJob, type PlayKind } from './rooms/jjim/bots';
import { AREAS, JDECK } from './rooms/jjim/layout';
import { createSpeech } from './ui/speech';
import { setLabel } from './ui/icons';
import { JJIM_TEXT, LAGOON_TEXT, MENU_TEXT, UI_TEXT, fill } from './text/en';

type RoomName = 'quiet' | 'lagoon' | 'jjim';
const MIST = 0xDCE5EC;
const canvas = document.getElementById('stage') as HTMLCanvasElement;
const stage = createStage(canvas, { clearColor: MIST, fog: [26, 52] });
const { scene, camera } = stage;
const lights = createLights(scene);
const now = () => stage.clock.getElapsedTime();

const room = createQuietRoom(scene);
const { R, ryokan, player } = room;
const cam = createFollowCamera(stage, lights, player.pos);
const marker = createTapMarker(room.world);
const speech = createSpeech(document.querySelector('.view') as HTMLElement);
const hit = new THREE.Vector3();
let cur: RoomName = 'quiet';
let lagoon: LagoonRoom | null = null;
let jjim: JjimRoom | null = null;

const navNow = (): NavGrid => (cur === 'lagoon' ? lagoon!.R.W.nav : cur === 'jjim' ? (player.size < 1 ? jjim!.S.navBaby : jjim!.S.nav) : R.nav);
const landingNow = (): LandingFn | undefined => (cur === 'quiet' ? R.landing : undefined);
const agentsNow = (): readonly Capy[] => (cur === 'lagoon' ? lagoon!.R.agents : cur === 'jjim' ? jjim!.R.agents : R.agents);
const jp = () => jjim!.player;

// ---------- 안내 문구 ----------
const hintEl = document.getElementById('hint')!;
let hintTimer = 0;
const restHint = () => { hintEl.textContent = cur === 'lagoon' ? LAGOON_TEXT.hint : cur === 'jjim' ? JJIM_TEXT.hint : ryokan.indoors ? UI_TEXT.hintIndoors : UI_TEXT.hint; };
function flashHint(msg: string, ms = 2600) {
  if (!msg) return;
  hintEl.textContent = msg;
  clearTimeout(hintTimer);
  hintTimer = window.setTimeout(restHint, ms);
}
function lagoonNote(key: LagoonNote) {
  const [a, b] = key.split('.') as [string, string?];
  const v = (LAGOON_TEXT as Record<string, unknown>)[a];
  flashHint(typeof v === 'string' ? v : (v as Record<string, string>)[b!]!, 4500);
}

// ---------- 탭: 카피바라 → 올라타기, 그 밖 → 걷기·탕·문 / 헤엄·바·폭포 (프로토타입 onTap 3916–3958) ----------
attachInput(canvas, cam, {
  onTap(cx, cy) {
    if (cur === 'quiet' && ryokan.indoors) return;
    const ray = rayFrom(canvas, camera, cx, cy), t = now();
    const others = agentsNow().filter((o) => o !== player && (o as { mode?: string }).mode !== 'indoors' && o.c.root.visible);
    const hits = ray.intersectObjects(others.flatMap((o) => [o.c.body, o.c.headG.children[0]!]), false);
    if (hits.length) {
      const T = hits[0]!.object.userData.agent as Capy, why = checkClimb(player, T);
      player.enterAfter = false;
      if (why !== 'ok') { flashHint(why === 'full' ? UI_TEXT.full : UI_TEXT.refused); return; }
      const go = () => { startClimb(player, T); };
      if (player.riding || player.hop) hopDown(player, t, navNow(), go, landingNow()); else go();
      return;
    }
    if (cur === 'jjim') {
      shopOpen = false;
      // 홀에서는 2층 데크 바닥(높이 JDECK.H)을 먼저 본다
      if (jp().area === 'hall' && ray.ray.intersectPlane(deckPlane, hit) && hit.x > JDECK.x0 && hit.x < JDECK.x1 && hit.z > JDECK.z0 && hit.z < JDECK.z1) { deckTap(jjim!.R, jp(), hit.x, hit.z); refresh(); return; }
      if (!hitGround(ray, hit)) return;
      const hx = hit.x, hz = hit.z, act = () => { if (jjimTap(jjim!.R, jp(), hx, hz)) marker.show(hx, hz); refresh(); };
      if (player.riding || player.hop) hopDown(player, t, navNow(), act); else act();
      return;
    }
    if (!hitGround(ray, hit)) return;
    player.enterAfter = false;
    const hx = hit.x, hz = hit.z;
    if (cur === 'lagoon') { lagoonTap(lagoon!.R, lagoon!.player, hx, hz, t); marker.show(hx, hz); refresh(); return; }
    const act = () => {
      if (isDoorTap(hx, hz)) { goIn(R, ryokan, player, now()); marker.show(DOOR.x, DOOR.z); return; }
      quietTap(R, player, hx, hz, now()); marker.show(hx, hz);
    };
    if (player.riding || player.hop) hopDown(player, t, R.nav, act, R.landing); else act();
  },
});

// ---------- 임시 버튼 ----------
const refreshers: (() => void)[] = [];
const refresh = () => refreshers.forEach((f) => f());
function button(label: string, click: () => void, pressed?: () => boolean, show?: () => boolean): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  setLabel(b, label);
  b.addEventListener('click', () => { click(); refresh(); });
  if (pressed) refreshers.push(() => b.setAttribute('aria-pressed', String(pressed())));
  if (show) refreshers.push(() => { b.hidden = !show(); });
  return b;
}
const row = (show?: () => boolean) => {
  const r = document.createElement('div'); r.className = 'row';
  if (show) refreshers.push(() => { r.hidden = !show(); });
  return r;
};

type Action = keyof typeof UI_TEXT.actions;
/** 동작 버튼 (프로토타입 play 3720–3747). */
function play(name: Action) {
  const a = player, t = now(), nav = navNow(), landing = landingNow();
  a.enterAfter = false;
  if (name === 'climbable') { a.allowRide = !a.allowRide; if (!a.allowRide) shakeOff(a, t, nav, undefined, landing); return; }
  if (name === 'hopdown') { hopDown(a, t, nav, undefined, landing); return; }
  if ((a.riding || a.hop) && (name === 'stroll' || name === 'bathe')) { hopDown(a, t, nav, () => { play(name); refresh(); }, landing); return; }
  if (a.mode === 'climb' && (name === 'stroll' || name === 'bathe')) { a.mode = 'idle'; a.climbT = null; }
  if (cur === 'jjim' && (name === 'stroll' || name === 'bathe')) return;
  if (cur === 'lagoon' && (name === 'stroll' || name === 'bathe')) {
    const lp = lagoon!.player, W = lagoon!.R.W;
    lp.onArrive = null; lp.napping = false;
    if (name === 'stroll') { lp.mode = lp.mode === 'stroll' ? 'idle' : 'stroll'; lp.wander = lp.mode === 'stroll' ? v3(lagoonRandom(W, lp.pos, null)) : null; }
    else { lp.mode = 'goto'; lp.wander = v3(lagoonRandom(W, lp.pos, true)); }
    return;
  }
  if (name === 'wobble' && a.rider) shakeOff(a, t, nav, undefined, landing);
  if (name === 'nap') { a.napping = !a.napping; a.emote = null; if (a.napping && a.mode === 'stroll') a.mode = 'idle'; }
  else if (name === 'stroll') {
    if (a.mode === 'stroll') a.mode = 'idle';
    else { releaseSlot(a); releaseWait(a); a.settled = false; a.mode = 'stroll'; a.wander = randomLand(R); }
    a.napping = false;
  }
  else if (name === 'bathe') { if (a.mode === 'bathe') startExit(R, a); else startBath(R, a, t); }
  else startEmote(a, name as EmoteKey, t);
}
const v3 = (p: { x: number; z: number }) => new THREE.Vector3(p.x, 0, p.z);

const PRESSED: Partial<Record<Action, () => boolean>> = {
  bathe: () => cur === 'quiet' && player.mode === 'bathe', stroll: () => player.mode === 'stroll', nap: () => player.napping, climbable: () => player.allowRide,
};
const inQuiet = () => cur === 'quiet', inLagoon = () => cur === 'lagoon', inJjim = () => cur === 'jjim';
const actRow = row(() => !(cur === 'quiet' && ryokan.indoors)), inRow = row(() => cur === 'quiet' && ryokan.indoors);
(Object.keys(UI_TEXT.actions) as Action[]).forEach((k) => {
  // 찜질방에는 목욕하기·산책하기가 없다 (자리를 탭해서 고른다)
  const b = button(UI_TEXT.actions[k], () => play(k), PRESSED[k], k === 'bathe' || k === 'stroll' ? () => !inJjim() : undefined);
  if (k === 'bathe') refreshers.push(() => { b.textContent = cur === 'lagoon' ? LAGOON_TEXT.swim : UI_TEXT.actions.bathe; });
  actRow.append(b);
});
actRow.append(button(UI_TEXT.goIn, () => goIn(R, ryokan, player, now()), undefined, inQuiet));
// 라군: 바·폭포로 헤엄쳐 가기, 컵 버리기
const lp = () => lagoon!.player;
(['mask', 'slush'] as BarJob[]).forEach((j) => actRow.append(button(LAGOON_TEXT.bars[j], () => { goToBar(lagoon!.R, lp(), j, now()); if (lp().onArrive === j) flashHint(j === 'mask' ? LAGOON_TEXT.toMask : LAGOON_TEXT.toSlush); },
  () => inLagoon() && lp().mode === 'goto' && lp().onArrive === j, inLagoon)));
actRow.append(button(LAGOON_TEXT.bars.rinse, () => { goToRinse(lagoon!.R, lp(), now()); if (lp().onArrive === 'rinse') flashHint(LAGOON_TEXT.toRinse); }, undefined, () => inLagoon() && !!player.mask));
actRow.append(button(LAGOON_TEXT.toss, () => { dropCarry(player, false, now()); flashHint(LAGOON_TEXT.tossed); }, undefined, () => inLagoon() && !!player.carry));
// 방 옮기기 (임시)
(['quiet', 'lagoon', 'jjim'] as RoomName[]).forEach((k) => actRow.append(button(UI_TEXT.goRoom[k], () => switchRoom(k), () => cur === k)));

// ---------- 찜질방: 구역 이동, 데크, 매점, 눕기·구르기, 때밀기·효자손 / 목욕탕 / 놀이터 ----------
const jjimRow = row(inJjim), jBathRow = row(() => inJjim() && jp().area === 'bath'), jPlayRow = row(() => inJjim() && jp().area === 'hall' && player.size < 1);
const routeTo = (k: string) => inJjim() && jp().mode === 'route' && jp().route?.job === 'enter' && jp().route?.area === k;
AREAS.forEach((k) => jjimRow.append(button(JJIM_TEXT.rooms[k], () => { shopOpen = false; goRoom(jjim!.R, jp(), k); }, () => routeTo(k))));
jjimRow.append(button(JJIM_TEXT.deck, () => goDeck(jjim!.R, jp()), () => inJjim() && (!!jp().stair || jp().deckMove?.then === 'spot' || jp().route?.job === 'deckUp')));
jjimRow.append(button(JJIM_TEXT.shop, () => goToShop(jjim!.R, jp())));
jjimRow.append(button(JJIM_TEXT.lie, () => toggleLieJ(jjim!.R, jp(), now()), () => inJjim() && !!player.lying));
jjimRow.append(button(JJIM_TEXT.rollL, () => startRollJ(jjim!.R, jp(), -1, now())));
jjimRow.append(button(JJIM_TEXT.rollR, () => startRollJ(jjim!.R, jp(), 1, now())));
jjimRow.append(button(JJIM_TEXT.scrub, () => { setHand(player, 'scrubmitt', 'jjim', jjim!.world); player.scrubT = now(); player.scratchT = 0; }));
jjimRow.append(button(JJIM_TEXT.scratch, () => { setHand(player, 'scratcher', 'jjim', jjim!.world); player.scratchT = now(); player.scrubT = 0; }));
(['wash', 'shower', 'hot', 'cold'] as BathJob[]).forEach((k) => jBathRow.append(button(JJIM_TEXT.bath[k], () => bathDo(jjim!.R, jp(), k),
  () => inJjim() && (k === 'hot' || k === 'cold' ? jp().spot?.kind === k || jp().after?.kind === k : jp().spot?.pose === k && !!(jp().spotPending || jp().washT || jp().jhop)))));
(['slide', 'tramp', 'pit'] as PlayKind[]).forEach((k) => jPlayRow.append(button(JJIM_TEXT.playBtn[k], () => playAt(jjim!.R, jp(), k), () => inJjim() && jp().spot?.kind === k)));

// 매점 메뉴: 4개까지 골라 주문 (3초 뒤 쟁반)
let shopOpen = false;
const cart: DishId[] = [];
const shopRow = row(() => inJjim() && shopOpen);
const orderBtn = button(JJIM_TEXT.order, () => {
  if (!cart.length) { flashHint(JJIM_TEXT.shopEmpty); return; }
  placeOrder(jjim!.R, jp(), cart.slice(), now()); shopOpen = false;
});
(Object.keys(MENUS.jjimshop) as DishId[]).forEach((k) => shopRow.append(button(MENU_TEXT[k].label, () => {
  const i = cart.indexOf(k);
  if (i >= 0) cart.splice(i, 1); else if (cart.length >= 4) { flashHint(JJIM_TEXT.shopMax); return; } else cart.push(k);
}, () => cart.includes(k))));
refreshers.push(() => { orderBtn.textContent = cart.length ? fill(JJIM_TEXT.orderN, { n: cart.length }) : JJIM_TEXT.order; });
shopRow.append(orderBtn);
function openShop() { cart.length = 0; shopOpen = true; jjimNote(JJIM_TEXT.shopPick); refresh(); }
const jjimNote = (m: string) => flashHint(m, 4500);
const deckPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -JDECK.H);
(Object.keys(MENUS.ryokan) as DishId[]).forEach((k) => inRow.append(button(MENU_TEXT[k].label, () => orderDish(player, k, room.world, now()))));
inRow.append(button(UI_TEXT.goOut, () => exitRoom(ryokan, player, cam, camera.aspect)));

// ---------- 라군 바 메뉴 (도착하면 열리고, 멀어지면 닫힌다) ----------
const barRow = row(() => inLagoon() && !!lagoon!.R.barOpen);
const barTitle = document.createElement('b'); barTitle.className = 'label';
let barShown: BarJob | null = null;
function openBar(job: BarJob) {
  barShown = job; barRow.replaceChildren(barTitle); barTitle.textContent = LAGOON_TEXT.barTitle[job];
  const menu = job === 'mask' ? MENUS.maskbar : MENUS.slushbar;
  (Object.keys(menu) as (LagoonMaskId | DishId)[]).forEach((k) => {
    const it = (menu as Record<string, { leaf: number }>)[k]!;
    const label = MENU_TEXT[k].label + ' · ' + (it.leaf ? `🍃${it.leaf}` : LAGOON_TEXT.free);
    barRow.append(button(label, () => { applyBarItem(lagoon!.R, lp(), job, k, now()); flashHint(job === 'mask' ? LAGOON_TEXT.maskOn : LAGOON_TEXT.slushOn, 4500); }));
  });
}

// ---------- 8글자 채팅 (a b c i m p r y, 띄어쓰기, 최대 16자) ----------
const chatRow = row(() => !inQuiet());
const draftEl = document.createElement('span'); draftEl.className = 'draft';
let draft = '';
function typeKey(k: string) {
  if (k === 'back') draft = draft.slice(0, -1);
  else if (k === 'send') {
    const msg = draft.trim();
    if (msg && isAllowedChat(msg)) {
      speech.say(player, msg, now()); draft = '';
      // 가까운 카피바라가 가끔 대답한다
      if (Math.random() < 0.6) {
        const near = agentsNow().filter((o) => !o.isPlayer && o.c.root.visible && Math.hypot(o.pos.x - player.pos.x, o.pos.z - player.pos.z) < 9);
        if (near.length) { const o = near[Math.floor(Math.random() * near.length)]!; setTimeout(() => speech.say(o, PHRASES[Math.floor(Math.random() * PHRASES.length)]!, now()), 1200 + Math.random() * 1200); }
      }
    }
  }
  else if (draft.length < CHAT_MAX) draft += k === 'space' ? (draft && !draft.endsWith(' ') ? ' ' : '') : k;
  draftEl.textContent = draft || LAGOON_TEXT.sayHint;
  draftEl.classList.toggle('empty', !draft);
}
chatRow.append(draftEl);
[...CHAT_LETTERS].forEach((ch) => chatRow.append(button(ch, () => typeKey(ch))));
(['space', 'back', 'send'] as const).forEach((k) => chatRow.append(button(LAGOON_TEXT.keys[k], () => typeKey(k))));
typeKey('');
window.addEventListener('keydown', (e) => {
  if (cur === 'quiet' || e.metaKey || e.ctrlKey || e.altKey) return;
  const k = e.key.toLowerCase();
  if (k.length === 1 && CHAT_LETTERS.includes(k)) { typeKey(k); e.preventDefault(); }
  else if (k === ' ') { typeKey('space'); e.preventDefault(); }
  else if (k === 'backspace') { typeKey('back'); e.preventDefault(); }
  else if (k === 'enter') { typeKey('send'); e.preventDefault(); }
});

document.getElementById('bar')!.append(barRow, shopRow, actRow, jjimRow, jBathRow, jPlayRow, inRow, chatRow);
setInterval(refresh, 500);

// ---------- 방 옮기기 (프로토타입 switchRoom 2738–2774) ----------
function switchRoom(name: RoomName) {
  if (name === cur) return;
  const a = player, t = now();
  if (cur === 'quiet' && ryokan.indoors) exitRoom(ryokan, player, cam, camera.aspect);
  shakeOff(a, t, navNow(), undefined, landingNow());
  const below = a.riding || (a.hop && a.hop.toBase); if (below) below.rider = null;
  a.riding = null; a.hop = null; a.rider = null; a.climbT = null;
  releaseSlot(a); releaseWait(a); cleanupEating(a); dropCarry(a, true, t);
  a.mode = 'idle'; a.wander = null; a.enterAfter = false; a.napping = false; a.emote = null; a.bathAmt = 0; a.washT = 0;
  speech.hide(agentsNow());
  if (lagoon) { lagoon.player.onArrive = null; lagoon.R.barOpen = null; lagoon.R.rinseUser = null; }
  if (cur === 'lagoon') lagoon!.day.reset();
  if (cur === 'jjim') { jjim!.leave(); setHand(a, 'none', 'quiet', scene); a.scratchT = 0; shopOpen = false; endDineView(); }
  // 라군 팩은 라군 밖에서 지운다
  if (a.faceKey && a.faceKey in MENUS.maskbar) setFace(a, a.wornFace ?? 'none');
  cur = name;
  if (name === 'lagoon' && !lagoon) {
    lagoon = createLagoonRoom(scene, stage, lights, player, { say: (o, text, t2) => speech.say(o, text, t2), note: lagoonNote, onBar: openBar });
  }
  if (name === 'jjim' && !jjim) {
    jjim = createJjimRoom(scene, stage, player, { say: (o, text, t2) => speech.say(o, text, t2), note: jjimNote, hint: (m) => flashHint(m), onShop: openShop });
  }
  room.world.visible = name === 'quiet';
  room.outside.forEach((o) => { o.visible = o === room.snow.points ? name !== 'jjim' : name === 'quiet'; });
  if (lagoon) lagoon.world.visible = name === 'lagoon';
  if (jjim) jjim.world.visible = name === 'jjim';
  if (name === 'lagoon') lagoon!.enter();
  else if (name === 'jjim') jjim!.enter();
  else {
    room.world.add(a.c.root, a.ripple);
    a.pos.set(2.8, 0, 4.4); a.c.root.position.copy(a.pos); a.facing = a.targetFacing = 0; a.path = null;
  }
  // 찜질방은 실내 조명, 나머지는 낮 (라군은 들어간 뒤 낮밤이 바꾼다)
  if (name === 'jjim') { lights.hemi.intensity = 0.62 * LEGACY; lights.key.intensity = 0.42 * LEGACY; lights.key.color.set(0xFFE6C8); stage.fog?.color.set(0x2A2230); stage.renderer.setClearColor(0x14182E, 1); }
  else { lights.hemi.intensity = 0.55 * LEGACY; lights.key.intensity = 0.58 * LEGACY; lights.key.color.set(0xFFF1DE); stage.fog?.color.set(MIST); stage.renderer.setClearColor(MIST, 1); }
  (name === 'lagoon' ? lagoon!.world : name === 'jjim' ? jjim!.world : room.world).add(marker.mesh);
  cam.yaw = 0; cam.pitch = 0.32; cam.snap();
  canvas.setAttribute('aria-label', UI_TEXT.canvas[name]);
  restHint(); refresh();
}

// ---------- 찜질방 식탁에서 먹는 동안: 카메라를 식탁 쪽으로 낮게 (끝나면 원래대로) ----------
let savedCam: { yaw: number; pitch: number; dist: number } | null = null;
function endDineView() {
  if (!savedCam) return;
  cam.yaw = savedCam.yaw; cam.pitch = savedCam.pitch; cam.dist = savedCam.dist; savedCam = null; cam.dine = false; cam.snap();
}

// ---------- 방 고르기 ----------
const pick = document.getElementById('roomPick')!;
document.getElementById('pickTitle')!.textContent = UI_TEXT.pickRoom;
(['quiet', 'lagoon', 'jjim'] as const).forEach((k) => {
  const b = document.createElement('button'), name = document.createElement('b'), blurb = document.createElement('span');
  b.type = 'button';
  name.textContent = UI_TEXT.rooms[k].name;
  blurb.textContent = UI_TEXT.rooms[k].blurb;
  b.append(name, blurb);
  b.addEventListener('click', () => { pick.hidden = true; switchRoom(k); });
  pick.querySelector('.rooms')!.append(b);
});
restHint(); refresh();

let wasIndoors = false;
stage.onTick.add((t, dt) => {
  if (cur === 'quiet') {
    room.update(t, dt, cam);
    if (ryokan.indoors !== wasIndoors) { wasIndoors = ryokan.indoors; restHint(); refresh(); }
  } else if (cur === 'lagoon') {
    lagoon!.update(t, dt);
    tickSnow(room.snow, t, dt, cam.target.x, cam.target.z);
    if (barShown !== lagoon!.R.barOpen) { if (lagoon!.R.barOpen) openBar(lagoon!.R.barOpen); else barShown = null; refresh(); }
  } else {
    jjim!.update(t, dt);
    if (jp().dineView) { if (!savedCam) { savedCam = { yaw: cam.yaw, pitch: cam.pitch, dist: cam.dist }; cam.snap(); } cam.yaw = player.facing; cam.pitch = 0.24; cam.dist = 6.2; cam.dine = true; }
    else endDineView();
  }
  cam.update(dt, player.c.root);
  marker.update(dt);
  speech.update(t, agentsNow(), camera, document.querySelector('.view') as HTMLElement);
});
stage.start();

// 개발 중 브라우저 콘솔에서 상태를 볼 수 있게 (배포 빌드에는 빠진다)
if (import.meta.env.DEV) Object.assign(window, { __capy: { stage, room, player, cam, get lagoon() { return lagoon; }, get jjim() { return jjim; }, switchRoom } });
